import { useEffect, useRef, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { ScanLine, Camera, X, UserCheck, PackageCheck, History } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none w-full";
const ST_STYLE = { in_use: "bg-emerald-50 text-emerald-700 border-emerald-200", in_stock: "bg-sky-50 text-sky-700 border-sky-200",
  in_repair: "bg-amber-50 text-amber-700 border-amber-200", retired: "bg-neutral-100 text-neutral-500 border-neutral-200" };

export const AssetScan = ({ members, onClose, onChanged }) => {
  const [code, setCode] = useState("");
  const [asset, setAsset] = useState(null);
  const [memberId, setMemberId] = useState("");
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const inputRef = useRef(null);
  const camSupported = typeof window !== "undefined" && "BarcodeDetector" in window;

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => () => stopCam(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (value) => {
    const v = (value ?? code).trim();
    if (!v) return;
    try {
      const { data } = await api.get(`/helpdesk/assets/scan?code=${encodeURIComponent(v)}`);
      setAsset(data);
      setMemberId(data.assigned_to_id || "");
    } catch (e) { setAsset(null); toast.error(fmtErr(e)); }
  };

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };

  const startCam = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCamOn(true);
    } catch { toast.error("Camera unavailable — use a USB scanner or type the tag"); }
  };

  useEffect(() => {
    if (!camOn || !videoRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
    const detector = new window.BarcodeDetector({ formats: ["code_128", "qr_code", "ean_13", "code_39", "upc_a"] });
    const iv = setInterval(async () => {
      try {
        const codes = await detector.detect(videoRef.current);
        if (codes.length) {
          const v = codes[0].rawValue;
          stopCam();
          setCode(v);
          lookup(v);
        }
      } catch { /* frame not ready */ }
    }, 350);
    return () => clearInterval(iv);
  }, [camOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async () => {
    if (!memberId) { toast.error("Pick a member to check out to"); return; }
    try {
      const { data } = await api.post(`/helpdesk/assets/${asset.id}/checkout`, { member_id: memberId });
      setAsset(data);
      toast.success(`${data.name} checked out to ${data.assigned_to_name}`);
      onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const checkin = async () => {
    try {
      const { data } = await api.post(`/helpdesk/assets/${asset.id}/checkin`);
      setAsset(data);
      setMemberId("");
      toast.success(`${data.name} checked in — back in stock`);
      onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="asset-scan-modal">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold font-heading text-lg flex items-center gap-2"><ScanLine size={18} /> Scan asset</h3>
          <button data-testid="scan-close" onClick={() => { stopCam(); onClose(); }} className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-400 mb-4">Scan a label with a USB scanner, use your camera, or type the asset tag.</p>
        <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="flex gap-2">
          <input ref={inputRef} data-testid="scan-code-input" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="Asset tag e.g. AST-1A2B3C" className={inputCls + " font-mono"} />
          <button data-testid="scan-lookup-button" className="px-4 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Find</button>
          {camSupported && !camOn && (
            <button type="button" data-testid="scan-camera-button" onClick={startCam} title="Scan with camera"
              className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50"><Camera size={16} /></button>
          )}
        </form>
        {camOn && (
          <div className="mt-3 relative" data-testid="scan-camera-view">
            <video ref={videoRef} muted playsInline className="w-full rounded-lg border border-neutral-200 bg-black aspect-video object-cover" />
            <button onClick={stopCam} className="absolute top-2 right-2 px-2.5 py-1 bg-black/60 text-white text-xs rounded-md">Stop</button>
            <p className="text-[11px] text-neutral-400 mt-1">Point the camera at the barcode — it's detected automatically.</p>
          </div>
        )}

        {asset && (
          <div className="mt-5 border border-neutral-200 rounded-xl p-4" data-testid="scan-result">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-sm">{asset.name}</p>
                <p className="text-xs text-neutral-400 font-mono mt-0.5">{asset.tag || asset.id.slice(0, 8)} · {asset.asset_type.replace(/_/g, " ")}</p>
              </div>
              <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${ST_STYLE[asset.status] || ""}`}>{asset.status.replace(/_/g, " ")}</span>
            </div>
            <p className="text-xs text-neutral-500 mt-2" data-testid="scan-assigned">
              {asset.assigned_to_name ? <>Checked out to <b>{asset.assigned_to_name}</b></> : "Not checked out — in stock"}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <select data-testid="scan-member-select" value={memberId} onChange={(e) => setMemberId(e.target.value)} className={inputCls + " w-auto flex-1 min-w-[140px]"}>
                <option value="">Check out to...</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button data-testid="scan-checkout-button" onClick={checkout}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium"><UserCheck size={13} /> Check out</button>
              {asset.assigned_to_id && (
                <button data-testid="scan-checkin-button" onClick={checkin}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-medium"><PackageCheck size={13} /> Check in</button>
              )}
            </div>
            {asset.history?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-neutral-100" data-testid="scan-history">
                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 flex items-center gap-1 mb-2"><History size={11} /> Recent activity</p>
                <ul className="space-y-1.5">
                  {[...asset.history].reverse().slice(0, 6).map((h, i) => (
                    <li key={i} className="text-xs text-neutral-500">
                      <span className={`font-semibold ${h.action === "checkout" ? "text-amber-700" : "text-emerald-700"}`}>{h.action === "checkout" ? "Checked out" : "Checked in"}</span>
                      {h.action === "checkout" ? ` to ${h.member_name}` : h.from_name ? ` from ${h.from_name}` : ""} · by {h.by} · {new Date(h.at).toLocaleString()}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
